import React, { useState, useEffect } from 'react';
import { Plus, FileText, Printer, Trash2, Edit, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SavedAVV, AVV_STORAGE_KEY, getIncompleteAVVFields } from '@/lib/constants/avvTemplateData';
import AVVForm from './AVVForm';
import AVVPreview from './AVVPreview';
import AVVDocumentRenderer from './AVVDocumentRenderer';

type View = 'list' | 'form' | 'preview' | 'print';

const AVVDashboard = () => {
  const [savedAVVs, setSavedAVVs] = useState<SavedAVV[]>([]);
  const [currentView, setCurrentView] = useState<View>('list');
  const [selectedAVV, setSelectedAVV] = useState<SavedAVV | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(AVV_STORAGE_KEY);
    if (stored) {
      try { setSavedAVVs(JSON.parse(stored)); } catch { /* ignore */ }
    }
  }, []);

  const persistAVVs = (avvs: SavedAVV[]) => {
    setSavedAVVs(avvs);
    localStorage.setItem(AVV_STORAGE_KEY, JSON.stringify(avvs));
  };

  const handleSave = (avv: SavedAVV) => {
    const exists = savedAVVs.findIndex(a => a.id === avv.id);
    const updated = exists >= 0
      ? savedAVVs.map(a => a.id === avv.id ? { ...avv, updatedAt: new Date().toISOString() } : a)
      : [...savedAVVs, avv];
    persistAVVs(updated);
    setSelectedAVV(avv);
    setCurrentView('preview');
  };

  const handleDelete = (id: string) => {
    persistAVVs(savedAVVs.filter(a => a.id !== id));
  };

  const handleFinalize = (id: string) => {
    persistAVVs(savedAVVs.map(a => a.id === id ? { ...a, status: 'finalized' as const, updatedAt: new Date().toISOString() } : a));
    setSelectedAVV(prev => prev && prev.id === id ? { ...prev, status: 'finalized' } : prev);
  };

  if (currentView === 'form') {
    return (
      <AVVForm
        existingAVV={selectedAVV}
        onSave={handleSave}
        onCancel={() => { setCurrentView('list'); setSelectedAVV(null); }}
      />
    );
  }

  if (currentView === 'preview' && selectedAVV) {
    return (
      <AVVPreview
        avv={selectedAVV}
        onBack={() => setCurrentView('list')}
        onEdit={() => setCurrentView('form')}
        onPrint={() => setCurrentView('print')}
        onFinalize={() => handleFinalize(selectedAVV.id)}
      />
    );
  }

  if (currentView === 'print' && selectedAVV) {
    return (
      <AVVDocumentRenderer
        avv={selectedAVV}
        onBack={() => setCurrentView('preview')}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Auftragsverarbeitungsverträge</h1>
          <p className="text-muted-foreground mt-1">
            AVV-Verwaltung gemäß Art. 28 DSGVO
          </p>
        </div>
        <Button onClick={() => { setSelectedAVV(null); setCurrentView('form'); }}>
          <Plus className="h-4 w-4 mr-2" />
          Neuen AVV erstellen
        </Button>
      </div>

      {savedAVVs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Noch keine AVVs erstellt</h3>
            <p className="text-muted-foreground mb-4 max-w-md">
              Erstellen Sie einen neuen Auftragsverarbeitungsvertrag für Ihre Kooperationspartner.
              Alle 13 Pflichtklauseln nach Art. 28 DSGVO werden automatisch generiert.
            </p>
            <Button onClick={() => { setSelectedAVV(null); setCurrentView('form'); }}>
              <Plus className="h-4 w-4 mr-2" />
              Ersten AVV erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {savedAVVs.map(avv => (
            <Card key={avv.id} className="hover:border-primary/50 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{avv.controller.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{avv.project.projectName}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {avv.status === 'draft' && getIncompleteAVVFields(avv).length > 0 && (
                      <Badge variant="outline" className="text-yellow-600 border-yellow-500 dark:text-yellow-400">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Unvollständig
                      </Badge>
                    )}
                    <Badge variant={avv.status === 'finalized' ? 'default' : 'secondary'}>
                      {avv.status === 'finalized' ? 'Finalisiert' : 'Entwurf'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Erstellt: {new Date(avv.createdAt).toLocaleDateString('de-DE')}
                    {avv.updatedAt !== avv.createdAt && (
                      <> · Aktualisiert: {new Date(avv.updatedAt).toLocaleDateString('de-DE')}</>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedAVV(avv); setCurrentView('preview'); }}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Ansehen
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedAVV(avv); setCurrentView('form'); }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Bearbeiten
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSelectedAVV(avv); setCurrentView('print'); }}
                    >
                      <Printer className="h-4 w-4 mr-1" />
                      Drucken
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(avv.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AVVDashboard;
